import { Capacitor } from '@capacitor/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/capacitor';
import 'cordova-plugin-purchase';
import { useAuthState } from 'state/useAuthState';
import {
  processIapPurchaseApi,
  upgradeMembershipApi,
  classifyIapReconcileError,
} from 'features/payments/payments.utils';
import {
  getPersistedIapState,
  IapCheckoutDetails,
  useIapState,
} from 'features/payments/hooks/useIapState';
import {
  findReconcilableTransaction,
  ReconcileCriteria,
} from 'features/payments/utils/reconcileInterruptedIap';
import { History } from 'history';

const MAX_PENDING_AGE_MS = 1000 * 60 * 60 * 24 * 30; // 30 days
const RESTORE_TIMEOUT_MS = 3_000;
const POLL_INTERVAL_MS = 150;

/**
 * Result of a reconciliation pass, consumed by the runner effect:
 * - `done`: terminal for this launch (nothing in flight, dropped, provisioned,
 *   or failed in a way we retry next launch) - mark complete, don't run again.
 * - `awaiting-auth`: preconditions not met yet (an upgrade is waiting for the
 *   original buyer to log in) - stay incomplete so an auth change reruns it.
 */
type ReconcileOutcome = 'done' | 'awaiting-auth';

const finishTransactionSafely = async (
  transaction: CdvPurchase.Transaction,
): Promise<void> => {
  try {
    await transaction.finish();
  } catch (err) {
    Sentry.captureException(err, {
      extra: { context: 'iap-reconcile-finish-transaction' },
    });
  }
};

/**
 * Cold launches may not have the transaction in `localTransactions` yet: force
 * a replay, then poll briefly. Fails open on error/timeout.
 */
const restoreAndFindTransaction = (
  criteria: ReconcileCriteria,
): Promise<CdvPurchase.Transaction | null> =>
  new Promise<CdvPurchase.Transaction | null>((resolve) => {
    CdvPurchase.store
      .restorePurchases()
      .catch(() => {
        /* fail open */
      })
      .finally(() => {
        const start = Date.now();
        const intervalId = setInterval(() => {
          const match = findReconcilableTransaction(
            CdvPurchase.store.localTransactions,
            criteria,
          );
          if (match || Date.now() - start >= RESTORE_TIMEOUT_MS) {
            clearInterval(intervalId);
            resolve(match);
          }
        }, POLL_INTERVAL_MS);
      });
  });

/**
 * Finds the charged-but-unfinished transaction. `expired` means no charge is
 * present and the checkout is too old to still have one (abandoned/cancelled).
 */
const resolveChargedTransaction = async (
  criteria: ReconcileCriteria,
  checkoutStartedAt: number,
): Promise<{
  transaction: CdvPurchase.Transaction | null;
  expired: boolean;
}> => {
  // Cheap local cache first; only force a restore when it isn't there.
  const cached = findReconcilableTransaction(
    CdvPurchase.store.localTransactions,
    criteria,
  );
  if (cached) {
    return { transaction: cached, expired: false };
  }

  if (Date.now() - checkoutStartedAt > MAX_PENDING_AGE_MS) {
    return { transaction: null, expired: true };
  }

  return {
    transaction: await restoreAndFindTransaction(criteria),
    expired: false,
  };
};

/**
 * Replays the call the interrupted checkout never finished.
 */
const provisionInterruptedPurchase = (
  isUpgrade: boolean,
  checkoutDetails: IapCheckoutDetails,
  selectedProductId: string,
  userId: number | undefined,
): Promise<void> => {
  if (isUpgrade) {
    return upgradeMembershipApi({
      verificationToken: checkoutDetails.verificationToken,
      userId: userId as number,
      upgradePlanCode: selectedProductId,
      fromIap: true,
    });
  }

  return processIapPurchaseApi({
    clientToken: checkoutDetails.clientToken,
    externalAccountCode: checkoutDetails.externalAccountCode,
    email: checkoutDetails.email,
    firstName: checkoutDetails.firstName,
    lastName: checkoutDetails.lastName,
    optInToUpdates: checkoutDetails.optInToUpdates,
    isRecurring: true,
    planCode: selectedProductId,
    isReconciliation: true,
  });
};

/**
 * Routes to the success page, which owns reset, login, and membership refresh.
 * A logged-out new purchase carries its client token so the page can verify and
 * log the user in; an upgrade or already-authenticated purchase does not.
 */
const finalizeToSuccessPage = ({
  history,
  isAuthenticated,
  isUpgrade,
  checkoutDetails,
}: {
  history: History;
  isAuthenticated: boolean;
  isUpgrade: boolean;
  checkoutDetails: IapCheckoutDetails;
}): void => {
  let url = '/membership/iap_success';
  if (isUpgrade) {
    url += '?upgrade=true';
  } else if (!isAuthenticated) {
    url += `?clientToken=${checkoutDetails.clientToken}`;
  }
  history.push(url);
};

/**
 * Recovers an iOS purchase Apple charged but whose provisioning never completed
 * (app killed or network dropped before `iap_create`/`upgrade_membership`).
 * Runs once the store is ready, and again when auth state changes.
 *
 * A lingering `checkoutStartedAt` marks an in-flight purchase; `purchaseType`
 * picks the replay (`new` -> `iap_create`, `upgrade` -> `upgrade_membership`).
 * Both route to the success page, which owns reset and membership refresh.
 */
export const useReconcileInterruptedIap = (): void => {
  const { isAuthenticated, user } = useAuthState();
  const { resetIapState } = useIapState();
  const history = useHistory();

  const userId = user?.id;

  const [storeReady, setStoreReady] = useState(false);
  const runningRef = useRef(false);
  const completedRef = useRef(false);

  const reconcile = useCallback(async (): Promise<ReconcileOutcome> => {
    const iapState = getPersistedIapState();
    const checkoutStartedAt = iapState?.checkoutDetails.checkoutStartedAt ?? 0;

    if (!iapState || !checkoutStartedAt) {
      return 'done';
    }

    const { checkoutDetails, selectedProductId } = iapState;
    const isUpgrade = checkoutDetails.purchaseType === 'upgrade';
    const criteria = { productId: selectedProductId, checkoutStartedAt };

    if (
      isUpgrade &&
      (!isAuthenticated || userId !== checkoutDetails.accountUserId)
    ) {
      return 'awaiting-auth';
    }

    const { transaction, expired } = await resolveChargedTransaction(
      criteria,
      checkoutStartedAt,
    );
    if (expired) {
      resetIapState();
      return 'done';
    }
    if (!transaction) {
      // Nothing charged, or not replayed yet - retry on a future launch.
      return 'done';
    }

    // A real charge exists: provision it regardless of age.
    try {
      await provisionInterruptedPurchase(
        isUpgrade,
        checkoutDetails,
        selectedProductId,
        userId,
      );
    } catch (err) {
      if (classifyIapReconcileError(err, isUpgrade) === 'already-provisioned') {
        // A prior attempt succeeded server-side: finish the receipt and
        // finalize like a fresh success.
        await finishTransactionSafely(transaction);
        Sentry.captureMessage('iap-reconcile-already-provisioned', {
          level: 'info',
        });
        finalizeToSuccessPage({
          history,
          isAuthenticated,
          isUpgrade,
          checkoutDetails,
        });
        return 'done';
      }

      // Transient/unknown failure - keep state + receipt to retry; never drop
      // a real Apple charge.
      Sentry.captureException(err, {
        extra: { context: 'iap-reconcile-create-purchase' },
      });
      return 'done';
    }

    await finishTransactionSafely(transaction);
    Sentry.captureMessage('iap-reconcile-success', { level: 'info' });
    finalizeToSuccessPage({
      history,
      isAuthenticated,
      isUpgrade,
      checkoutDetails,
    });
    return 'done';
  }, [isAuthenticated, userId, resetIapState, history]);

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') {
      return undefined;
    }

    const onDeviceReady = (): void => {
      CdvPurchase.store.ready(() => setStoreReady(true));
    };

    document.addEventListener('deviceready', onDeviceReady);
    return () => {
      document.removeEventListener('deviceready', onDeviceReady);
    };
  }, []);

  useEffect(() => {
    if (!storeReady || completedRef.current || runningRef.current) {
      return;
    }

    runningRef.current = true;
    reconcile()
      .then((outcome) => {
        // Only `done` stops further runs; `awaiting-auth` leaves this
        // incomplete so a later auth change (deps) reruns reconciliation.
        if (outcome === 'done') {
          completedRef.current = true;
        }
      })
      .catch((err) => {
        Sentry.captureException(err, {
          extra: { context: 'iap-reconcile-run' },
        });
      })
      .finally(() => {
        runningRef.current = false;
      });
  }, [storeReady, reconcile]);
};
